import { dateLib } from "@agentcall/core/lib"
import { Suspense, useState } from "react"
import { ArrowLeft, Clock, Shield } from "react-feather"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { type OwnerModels, ownerApi, ownerProperties } from "@/api/client/owner"
import { Button } from "@/components/ui/button"
import { EmptyCard } from "@/components/ui/card"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { SectionLoading } from "@/components/ui/loading"
import { PaginationSection } from "@/components/ui/pagination-section"
import { OperationRuleDetailBody } from "../components/OperationRuleDetailBody"
import { OwnerHistoryModal } from "../components/OwnerHistoryModal"

type OperationRuleLogItem = OwnerModels.OwnerGet200OwnerOperationRulesItemOperationRuleLogsItem

const OperationRuleDetailContent = ({
  ownerId,
  propertyId,
}: {
  ownerId: string
  propertyId: number | null
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedLog, setSelectedLog] = useState<OperationRuleLogItem | null>(null)

  // オーナー取得に運用ルール(全スコープ)と変更履歴、物件名解決用の物件一覧を同梱する(E-13)
  const {
    data: { owner },
  } = ownerApi.useOwnerGetSuspense(ownerId, {
    withProperties: ownerProperties.OwnerGetWithProperties.true,
    withOperationRulesOperationRuleLogs:
      ownerProperties.OwnerGetWithOperationRulesOperationRuleLogs.true,
  })
  const operationRule =
    (owner.operationRules ?? []).find((rule) => (rule.propertyId ?? null) === propertyId) ?? null

  const scopeLabel =
    propertyId == null
      ? "既定（すべての物件に適用）"
      : (owner.properties?.find((property) => property.id === propertyId)?.name ??
        `物件ID: ${propertyId}`)

  // 変更履歴は API が新しい順で返す。
  const logs = operationRule?.operationRuleLogs ?? []
  const itemsPerPage = 10
  const paginatedLogs = logs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  return (
    <>
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">運用ルール詳細</h1>
        <span className="text-sm text-slate-500">
          {owner.name} ／ {scopeLabel}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-3">
        {/* 現在の設定 */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3 h-3" />
              <span className="text-sm font-semibold text-slate-600">現在の設定</span>
            </div>
            <span className="text-xs text-slate-500">
              最終更新：{operationRule ? dateLib.formatDateTime(operationRule.updatedAt) : "-"}
            </span>
          </div>
          <div className="p-5">
            {operationRule ? (
              <OperationRuleDetailBody operationRule={operationRule} />
            ) : (
              <EmptyCard text="運用ルールは設定されていません" />
            )}
          </div>
        </div>

        {/* 変更履歴 */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between p-5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              <span className="text-sm font-semibold text-slate-600">変更履歴</span>
            </div>
            <span className="text-xs text-slate-500">{logs.length}件（新しい順）</span>
          </div>
          <div className="p-5">
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
                <span className="text-sm font-semibold text-slate-600">更新日時</span>
              </div>
              {paginatedLogs.length === 0 && (
                <EmptyCard className="m-5" text="変更履歴はありません" />
              )}
              <ul>
                {paginatedLogs.map((log) => (
                  <li
                    key={log.id}
                    className="flex items-center justify-between px-4 py-3 border-b border-slate-200 last:border-b-0"
                  >
                    <p className="text-sm">
                      {dateLib.formatDateTime(log.createdAt)}
                      {log.deleted && <span className="ml-2 text-xs text-red-600">削除</span>}
                    </p>
                    <Button variant="outline" size="sm" onClick={() => setSelectedLog(log)}>
                      詳細
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
            <PaginationSection
              currentPage={currentPage}
              totalCount={logs.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <OwnerHistoryModal
        open={selectedLog !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedLog(null)
        }}
        selectedOperationHistory={selectedLog}
      />
    </>
  )
}

export const OperationRuleDetailPage = () => {
  const { ownerId = "" } = useParams<{ ownerId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const propertyIdParam = searchParams.get("propertyId")
  const propertyId = propertyIdParam ? Number(propertyIdParam) : null

  return (
    <div className="space-y-6">
      {/* Header - 戻るボタン */}
      <div className="flex items-center gap-2.5">
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7 shadow-sm"
          onClick={() => navigate(`/owners/${ownerId}`)}
        >
          <ArrowLeft size={14} />
        </Button>
        <h2 className="text-sm font-semibold text-slate-600">オーナー詳細に戻る</h2>
      </div>

      <ReactErrorBoundary>
        <Suspense fallback={<SectionLoading />}>
          <OperationRuleDetailContent ownerId={ownerId} propertyId={propertyId} />
        </Suspense>
      </ReactErrorBoundary>
    </div>
  )
}
